<script setup>
import { computed, h } from 'vue';
import {
  useVueTable,
  createColumnHelper,
  getCoreRowModel,
} from '@tanstack/vue-table';
import { useI18n } from 'vue-i18n';

import Spinner from 'shared/components/Spinner.vue';
import EmptyState from 'dashboard/components/widgets/EmptyState.vue';
import Table from 'dashboard/components/table/Table.vue';
import Pagination from 'dashboard/components/table/Pagination.vue';
import UserActivityCell from './UserActivityCell.vue';

const { userSummary, pageIndex } = defineProps({
  userSummary: {
    type: Array,
    default: () => [],
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
  pageIndex: {
    type: Number,
    default: 1,
  },
});

const emit = defineEmits(['pageChange']);
const { t } = useI18n();

const formatDuration = minutes => {
  if (!minutes || minutes === 0) return '0m';

  if (minutes < 60) {
    return `${Math.round(minutes)}m`;
  }
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = Math.round(minutes % 60);
  return `${hours}h ${remainingMinutes}m`;
};

const getPauseCountBadgeClass = count => {
  if (count === 0) {
    return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
  }
  if (count <= 5) {
    return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
  }
  if (count <= 10) {
    return 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200';
  }
  return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
};

const totalCount = computed(() => userSummary.length);

const tableData = computed(() => {
  return userSummary.map(user => ({
    user_id: user.user_id,
    user_name: user.user_name,
    total_pause_sessions: user.total_pause_sessions,
    average_pause_duration_minutes: user.average_pause_duration_minutes,
    total_pause_duration_minutes: user.total_pause_duration_minutes,
  }));
});

const pauseCountRender = cellProps => {
  const count = cellProps.getValue();
  return h(
    'span',
    {
      class: `inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getPauseCountBadgeClass(count)}`,
    },
    count || 0
  );
};

const durationRender = cellProps => {
  const minutes = cellProps.getValue();
  return h(
    'span',
    {
      class: 'text-slate-800 dark:text-slate-200',
    },
    formatDuration(minutes)
  );
};

const columnHelper = createColumnHelper();
const columns = [
  columnHelper.accessor('user_name', {
    header: t('USER_ACTIVITY_REPORTS.TABLE.AGENT_NAME'),
    cell: cellProps => h(UserActivityCell, cellProps),
    size: 250,
  }),
  columnHelper.accessor('total_pause_sessions', {
    header: t('USER_ACTIVITY_REPORTS.TABLE.PAUSE_COUNT'),
    cell: pauseCountRender,
    size: 150,
  }),
  columnHelper.accessor('average_pause_duration_minutes', {
    header: t('USER_ACTIVITY_REPORTS.TABLE.AVG_PAUSE_DURATION'),
    cell: durationRender,
    size: 180,
  }),
  columnHelper.accessor('total_pause_duration_minutes', {
    header: t('USER_ACTIVITY_REPORTS.TABLE.TOTAL_PAUSE_DURATION'),
    cell: durationRender,
    size: 180,
  }),
];

const paginationParams = computed(() => {
  return {
    pageIndex: pageIndex,
    pageSize: 25,
  };
});

const table = useVueTable({
  get data() {
    return tableData.value;
  },
  columns,
  manualPagination: true,
  enableSorting: true,
  getCoreRowModel: getCoreRowModel(),
  get rowCount() {
    return totalCount.value;
  },
  state: {
    get pagination() {
      return paginationParams.value;
    },
  },
  onPaginationChange: updater => {
    const newPagination = updater(paginationParams.value);
    emit('pageChange', newPagination.pageIndex);
  },
});
</script>

<template>
  <div class="user-activity-table-container">
    <Table
      :table="table"
      class="max-h-[calc(100vh-21.875rem)] border border-slate-50 dark:border-slate-800"
    />
    <Pagination class="mt-2" :table="table" />
    <div v-if="isLoading" class="user-activity-loader">
      <Spinner />
      <span>{{ $t('USER_ACTIVITY_REPORTS.LOADING_MESSAGE') }}</span>
    </div>
    <EmptyState
      v-else-if="!isLoading && !userSummary.length"
      :title="$t('USER_ACTIVITY_REPORTS.NO_DATA')"
    />
  </div>
</template>

<style lang="scss" scoped>
.user-activity-table-container {
  display: flex;
  flex-direction: column;
  flex: 1;

  .ve-table {
    &::v-deep {
      th.ve-table-header-th {
        font-size: var(--font-size-mini) !important;
        padding: var(--space-small) var(--space-two) !important;
      }

      td.ve-table-body-td {
        padding: var(--space-one) var(--space-two) !important;
      }
    }
  }

  &::v-deep .ve-pagination {
    background-color: transparent;
  }

  &::v-deep .ve-pagination-select {
    display: none;
  }

  .row-user-block {
    display: flex;
    align-items: center;
    text-align: left;

    .user-block {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      min-width: 0;
      margin: 0 8px;

      .title {
        font-size: 14px;
        margin: 0;
        line-height: 1.2;
        color: var(--s-800);
      }

      .sub-title {
        font-size: 12px;
        color: var(--s-600);
      }
    }
  }

  .table-pagination {
    margin-top: 16px;
    text-align: right;
  }
}

.user-activity-loader {
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  padding: 32px;
}
</style>
